import { useMemo, useState } from "react";
import { cn } from "../../lib/utils";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import { EmptyState } from "../ui/empty-state";
import { Field } from "../ui/field";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { PanelHeader } from "./PanelHeader";
import { ExtractToolbar } from "./ExtractToolbar";
import { RosterGuidePanel } from "./RosterGuidePanel";
import {
  DISTANCE_UNITS,
  LOCATION_RELATIONSHIP_KINDS,
  LOCATION_RELATIONSHIP_LABELS,
  LOCATION_TYPE_LABELS,
  LOCATION_TYPES,
  childLocations,
  formatDistance,
  generateLocationRelationshipId,
  parentChain,
  relationshipsForLocation,
  sortLocations,
  type BookLocation,
  type DistanceUnit,
  type LocationRelationship,
  type LocationRelationshipKind,
  type LocationRemovalRecord,
  type LocationType,
} from "../../../shared/locationRoster";

const LOCATION_TYPE_STYLES: Record<LocationType, string> = {
  room: "bg-purple-500/15 text-foreground",
  building: "bg-blue-500/15 text-foreground",
  settlement: "bg-green-500/15 text-foreground",
  region: "bg-amber-500/15 text-foreground",
  landmark: "bg-secondary text-secondary-foreground",
  other: "bg-secondary text-secondary-foreground",
};

function LocationDetail({
  location,
  roster,
  relationships,
  chapters,
  onUpdate,
  onRequestRemove,
  onOpenChapter,
  onUpdateRelationships,
}: {
  location: BookLocation;
  roster: BookLocation[];
  relationships: LocationRelationship[];
  chapters: Array<{ id: string; order: number; title: string }>;
  onUpdate: (id: string, patch: Partial<BookLocation>) => void;
  onRequestRemove: (location: BookLocation) => void;
  onOpenChapter: (chapterId: string) => void;
  onUpdateRelationships: (next: LocationRelationship[]) => void;
}) {
  const chapterMeta = useMemo(() => {
    const map = new Map(chapters.map((c) => [c.id, c]));
    return [...location.appearances]
      .map((a) => map.get(a.chapterId))
      .filter((c): c is { id: string; order: number; title: string } =>
        Boolean(c),
      )
      .sort((a, b) => a.order - b.order);
  }, [location.appearances, chapters]);

  const parents = parentChain(roster, location.id);
  const children = childLocations(roster, location.id);
  const linked = relationshipsForLocation(relationships, location.id);
  const otherLocations = roster.filter((loc) => loc.id !== location.id);

  const [relToId, setRelToId] = useState(otherLocations[0]?.id ?? "");
  const [relKind, setRelKind] = useState<LocationRelationshipKind>("adjacent");
  const [relDistance, setRelDistance] = useState("");
  const [relUnit, setRelUnit] = useState<DistanceUnit>("mi");
  const [relTravel, setRelTravel] = useState("");
  const [relMode, setRelMode] = useState("");
  const [relDirection, setRelDirection] = useState("");
  const [relDescription, setRelDescription] = useState("");

  function addRelationship(): void {
    if (!relToId || relToId === location.id) return;
    const distance = relDistance.trim() ? Number(relDistance) : undefined;
    const next: LocationRelationship = {
      id: generateLocationRelationshipId(),
      fromLocationId: location.id,
      toLocationId: relToId,
      kind: relKind,
      ...(distance != null && Number.isFinite(distance) ? { distance } : {}),
      ...(distance != null && Number.isFinite(distance)
        ? { distanceUnit: relUnit }
        : {}),
      ...(relTravel.trim() ? { travelTime: relTravel.trim() } : {}),
      ...(relMode.trim() ? { travelMode: relMode.trim() } : {}),
      ...(relDirection.trim() ? { direction: relDirection.trim() } : {}),
      description: relDescription.trim(),
      updatedAt: new Date().toISOString(),
    };
    onUpdateRelationships([...relationships, next]);
    setRelDistance("");
    setRelTravel("");
    setRelMode("");
    setRelDirection("");
    setRelDescription("");
  }

  function removeRelationship(id: string): void {
    onUpdateRelationships(relationships.filter((rel) => rel.id !== id));
  }

  function updateRelationship(
    id: string,
    patch: Partial<LocationRelationship>,
  ): void {
    onUpdateRelationships(
      relationships.map((rel) =>
        rel.id === id
          ? { ...rel, ...patch, updatedAt: new Date().toISOString() }
          : rel,
      ),
    );
  }

  return (
    <div className="flex max-w-208 flex-col gap-3.5">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          aria-label="Location name"
          className="min-w-48 flex-1 text-[1.05rem] font-semibold"
          value={location.name}
          onChange={(e) => onUpdate(location.id, { name: e.target.value })}
        />
        <Select
          value={location.type}
          onValueChange={(value) =>
            onUpdate(location.id, { type: value as LocationType })
          }
        >
          <SelectTrigger size="default" aria-label="Location type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {LOCATION_TYPES.map((type) => (
              <SelectItem key={type} value={type}>
                {LOCATION_TYPE_LABELS[type]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          size="sm"
          variant="secondary"
          type="button"
          onClick={() => onRequestRemove(location)}
        >
          Remove
        </Button>
      </div>

      <Field label="Aliases (comma separated)">
        <Input
          aria-label="Aliases"
          value={location.aliases.join(", ")}
          onChange={(e) =>
            onUpdate(location.id, {
              aliases: e.target.value
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean),
            })
          }
        />
      </Field>

      <Field label="What this place is (book) — lasting identity for wiki and maps">
        <Textarea
          aria-label="Description"
          rows={4}
          placeholder="Layout, atmosphere, role in the story…"
          value={location.description}
          onChange={(e) =>
            onUpdate(location.id, { description: e.target.value })
          }
        />
      </Field>

      <Field label="Parent location (containment)">
        <Select
          value={location.parentLocationId ?? "__none__"}
          onValueChange={(value) =>
            onUpdate(location.id, {
              parentLocationId: value === "__none__" ? undefined : value,
            })
          }
        >
          <SelectTrigger size="default" aria-label="Parent location">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">None</SelectItem>
            {otherLocations.map((loc) => (
              <SelectItem key={loc.id} value={loc.id}>
                {loc.name} ({LOCATION_TYPE_LABELS[loc.type]})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>

      {(parents.length > 0 || children.length > 0) && (
        <section>
          <h3 className="text-sm font-medium">Hierarchy</h3>
          {parents.length > 0 ? (
            <p className="text-xs text-muted-foreground">
              Contained in: {parents.map((p) => p.name).join(" → ")}
            </p>
          ) : null}
          {children.length > 0 ? (
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {children.map((child) => (
                <Badge key={child.id} variant="outline" className="rounded-full">
                  {child.name}
                </Badge>
              ))}
            </div>
          ) : null}
        </section>
      )}

      <section>
        <h3 className="text-sm font-medium">Appears in</h3>
        {chapterMeta.length === 0 ? (
          <EmptyState compact className="mt-1.5" title="Not linked to any chapter yet." />
        ) : (
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {chapterMeta.map((ch) => (
              <Button
                key={ch.id}
                type="button"
                variant="outline"
                size="xs"
                className="rounded-full"
                onClick={() => onOpenChapter(ch.id)}
              >
                {ch.title}
              </Button>
            ))}
          </div>
        )}
      </section>

      <section className="flex flex-col gap-2.5">
        <h3 className="text-sm font-medium">Relationships & distance</h3>
        <p className="text-xs text-muted-foreground">
          Map-ready edges: adjacency, connections, routes, and proximity with
          optional distance.
        </p>
        {linked.length === 0 ? (
          <EmptyState compact title="No relationships yet." />
        ) : (
          <div className="flex flex-col gap-3">
            {linked.map((rel) => {
              const otherId =
                rel.fromLocationId === location.id
                  ? rel.toLocationId
                  : rel.fromLocationId;
              const other = roster.find((loc) => loc.id === otherId);
              return (
                <Card
                  key={rel.id}
                  size="sm"
                  className="gap-2.5 rounded-md bg-muted/10 p-2.5 py-2.5 ring-1 ring-border"
                >
                  <div className="flex flex-wrap items-center gap-1.5">
                    <strong className="text-xs">
                      {other?.name ?? "Unknown"}
                    </strong>
                    <Select
                      value={rel.kind}
                      onValueChange={(value) =>
                        updateRelationship(rel.id, {
                          kind: value as LocationRelationshipKind,
                        })
                      }
                    >
                      <SelectTrigger
                        className="min-w-26"
                        size="sm"
                        aria-label="Relationship kind"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {LOCATION_RELATIONSHIP_KINDS.map((kind) => (
                          <SelectItem key={kind} value={kind}>
                            {LOCATION_RELATIONSHIP_LABELS[kind]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      size="sm"
                      variant="secondary"
                      type="button"
                      onClick={() => removeRelationship(rel.id)}
                    >
                      Remove
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-1.5 *:min-w-22 *:flex-1">
                    <Input
                      aria-label="Distance"
                      placeholder="Distance"
                      value={rel.distance != null ? String(rel.distance) : ""}
                      onChange={(e) => {
                        const n = e.target.value.trim()
                          ? Number(e.target.value)
                          : undefined;
                        updateRelationship(rel.id, {
                          distance:
                            n != null && Number.isFinite(n) ? n : undefined,
                        });
                      }}
                    />
                    <Select
                      value={rel.distanceUnit ?? "mi"}
                      onValueChange={(value) =>
                        updateRelationship(rel.id, {
                          distanceUnit: value as DistanceUnit,
                        })
                      }
                    >
                      <SelectTrigger
                        className="min-w-18"
                        size="sm"
                        aria-label="Distance unit"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DISTANCE_UNITS.map((unit) => (
                          <SelectItem key={unit} value={unit}>
                            {unit}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      aria-label="Travel time"
                      placeholder="Travel time"
                      value={rel.travelTime ?? ""}
                      onChange={(e) =>
                        updateRelationship(rel.id, {
                          travelTime: e.target.value,
                        })
                      }
                    />
                    <Input
                      aria-label="Travel mode"
                      placeholder="Mode"
                      value={rel.travelMode ?? ""}
                      onChange={(e) =>
                        updateRelationship(rel.id, {
                          travelMode: e.target.value,
                        })
                      }
                    />
                    <Input
                      aria-label="Direction"
                      placeholder="Direction"
                      value={rel.direction ?? ""}
                      onChange={(e) =>
                        updateRelationship(rel.id, {
                          direction: e.target.value,
                        })
                      }
                    />
                  </div>
                  <Textarea
                    aria-label="Relationship description"
                    rows={2}
                    placeholder="Relationship note…"
                    value={rel.description}
                    onChange={(e) =>
                      updateRelationship(rel.id, {
                        description: e.target.value,
                      })
                    }
                  />
                  {formatDistance(rel) ? (
                    <p className="text-xs text-muted-foreground">
                      Distance: {formatDistance(rel)}
                    </p>
                  ) : null}
                </Card>
              );
            })}
          </div>
        )}

        <div className="mt-1.5 flex flex-col gap-1.5 border-t border-border pt-2.5">
          <div className="text-xs font-medium">Add relationship</div>
          <div className="flex flex-wrap gap-1.5 *:min-w-22 *:flex-1">
            <Select
              value={relToId}
              onValueChange={(value) => setRelToId(value)}
            >
              <SelectTrigger size="default" aria-label="Related location">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {otherLocations.map((loc) => (
                  <SelectItem key={loc.id} value={loc.id}>
                    {loc.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={relKind}
              onValueChange={(value) =>
                setRelKind(value as LocationRelationshipKind)
              }
            >
              <SelectTrigger
                className="min-w-26"
                size="sm"
                aria-label="New relationship kind"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LOCATION_RELATIONSHIP_KINDS.map((kind) => (
                  <SelectItem key={kind} value={kind}>
                    {LOCATION_RELATIONSHIP_LABELS[kind]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              aria-label="New distance"
              placeholder="Distance"
              value={relDistance}
              onChange={(e) => setRelDistance(e.target.value)}
            />
            <Select
              value={relUnit}
              onValueChange={(value) => setRelUnit(value as DistanceUnit)}
            >
              <SelectTrigger
                className="min-w-18"
                size="sm"
                aria-label="New distance unit"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DISTANCE_UNITS.map((unit) => (
                  <SelectItem key={unit} value={unit}>
                    {unit}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-wrap gap-1.5 *:min-w-22 *:flex-1">
            <Input
              aria-label="New travel time"
              placeholder="Travel time"
              value={relTravel}
              onChange={(e) => setRelTravel(e.target.value)}
            />
            <Input
              aria-label="New travel mode"
              placeholder="Mode"
              value={relMode}
              onChange={(e) => setRelMode(e.target.value)}
            />
            <Input
              aria-label="New direction"
              placeholder="Direction"
              value={relDirection}
              onChange={(e) => setRelDirection(e.target.value)}
            />
          </div>
          <Textarea
            aria-label="New relationship description"
            rows={2}
            placeholder="Relationship note…"
            value={relDescription}
            onChange={(e) => setRelDescription(e.target.value)}
          />
          <Button
            size="sm"
            type="button"
            onClick={addRelationship}
            disabled={!relToId}
          >
            Add relationship
          </Button>
        </div>
      </section>
    </div>
  );
}

export function LocationsGuidePanel({
  roster,
  relationships,
  removals,
  chapters,
  selectedId,
  onSelect,
  onUpdate,
  onUpdateRelationships,
  onRequestRemove,
  onAdd,
  holisticBusy,
  extractModel,
  onExtractModelChange,
  openAiModels,
  anthropicModels,
  onAnalyzeWholeBook,
  onOpenChapter,
}: {
  roster: BookLocation[];
  relationships: LocationRelationship[];
  removals: LocationRemovalRecord[];
  chapters: Array<{ id: string; order: number; title: string }>;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onUpdate: (id: string, patch: Partial<BookLocation>) => void;
  onUpdateRelationships: (next: LocationRelationship[]) => void;
  onRequestRemove: (location: BookLocation) => void;
  onAdd: () => void;
  holisticBusy: boolean;
  extractModel: string;
  onExtractModelChange: (
    model: string,
    provider: "openai" | "anthropic",
  ) => void;
  openAiModels: ReadonlyArray<{ value: string; label: string }>;
  anthropicModels: ReadonlyArray<{ value: string; label: string }>;
  onAnalyzeWholeBook: () => void;
  onOpenChapter: (chapterId: string) => void;
}) {
  const sorted = sortLocations(roster);

  return (
    <RosterGuidePanel
      ariaLabel="Locations guide"
      rosterAriaLabel="Location list"
      items={sorted}
      selectedId={selectedId}
      onSelect={onSelect}
      removals={removals}
      rosterEmpty="No locations yet. Extract from a chapter or add one here."
      detailFallback="Select a location to view its page."
      header={
        <PanelHeader
          title="Locations"
          description="Book gazetteer with hierarchy, relationships, and distance for wiki and maps."
          actions={
            <>
              <ExtractToolbar
                model={extractModel}
                onModelChange={onExtractModelChange}
                openAiModels={openAiModels}
                anthropicModels={anthropicModels}
                onExtract={onAnalyzeWholeBook}
                extractBusy={holisticBusy}
                extractLabel="Analyze book"
                extractBusyLabel="Analyzing…"
                modelClassName="min-w-[10rem]"
              />
              <Button size="sm" type="button" onClick={onAdd}>
                Add location
              </Button>
            </>
          }
        />
      }
      renderRosterItem={(location) => (
        <>
          <span className="truncate text-[0.85rem] font-semibold">
            {location.name}
          </span>
          <Badge
            variant="secondary"
            className={cn(
              "h-4 px-1.5 text-[0.65rem] uppercase tracking-[0.04em]",
              LOCATION_TYPE_STYLES[location.type]
            )}
          >
            {LOCATION_TYPE_LABELS[location.type]}
          </Badge>
          <span className="col-span-2 text-xs text-muted-foreground">
            {location.appearances.length} ch
            {location.description ? " · " : ""}
            {location.description ? (
              <>
                {location.description.slice(0, 80)}
                {location.description.length > 80 ? "…" : ""}
              </>
            ) : null}
          </span>
        </>
      )}
      renderDetail={(location) => (
        <LocationDetail
          location={location}
          roster={roster}
          relationships={relationships}
          chapters={chapters}
          onUpdate={onUpdate}
          onRequestRemove={onRequestRemove}
          onOpenChapter={onOpenChapter}
          onUpdateRelationships={onUpdateRelationships}
        />
      )}
    />
  );
}
